import { FunctionComponent } from 'react';
import clsx from 'clsx';
import styles from './ServiceOrComplementaryDataInfo.module.scss';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import Staff from 'model/Staff';
import ServiceOptions from './ServiceOptions';
import { DataVaraible, AppointmentMultiple, PetServiceWrapper, itemTypes, ItemData } from 'model/NewMultipleBooking';
import { Product } from '@spike/product-model';
import AddOnService from '@spike/addon-service-model';
import Service from 'model/Service';
import { Moment } from 'moment-timezone';
import { resolveAppointmentCardLineBase, resolveAppointmentCardLineDisplay } from '../utils/membershipDisplayPrice';

interface Props {
    data: DataVaraible;
    index: number;
    appts: Array<DataVaraible>;
    appointment: AppointmentMultiple;
    onEdit: (
        petId: number,
        dateEdit: Moment,
        staffId?: number,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes,
        dateEnd?: Moment
    ) => void;
    onDeleteAppointment: (petId: number, value?: number | string, type?: itemTypes, dateToRemove?: Moment) => void;
    onShowActions: (
        petId: number,
        dateEdit: Moment,
        itemData?: ItemData,
        service?: PetServiceWrapper,
        staffId?: number,
        product?: Product,
        addOn?: AddOnService,
        custom?: string,
        type?: itemTypes
    ) => void;
}

export const getServicesTime = (serv?: PetServiceWrapper) => {
    const timeInMinutes = serv?.durationInMinutes;
    return timeInMinutes
        ? timeInMinutes / 60 > 1
            ? `${Math.trunc(timeInMinutes! / 60)}hrs ${timeInMinutes! % 60 !== 0 ? timeInMinutes! % 60 : ''}${
                  timeInMinutes! % 60 !== 0 ? 'min' : ''
              }`
            : `${timeInMinutes! === 60 ? 60 : timeInMinutes! % 60}min`
        : '0';
};

export const getItem = (data: DataVaraible, type: itemTypes, edit?: boolean) => {
    const items = {
        [itemTypes.service]: edit ? 'Service' : getServicesTime(data.service),
        'add-on': 'Add-On',
        [itemTypes.product]: 'Product',
        [itemTypes.custom]: 'Custom'
    };
    return items[type] || undefined;
};

const getType = (data: DataVaraible) => {
    let type = itemTypes.service;
    if (data.service) {
        type = itemTypes.service;
    } else if (data.product) {
        type = itemTypes.product;
    } else if (data.addOn) {
        type = itemTypes.addOn;
    } else if (data.customDescription) {
        type = itemTypes.custom;
    }
    return type;
};

const getName = (data: DataVaraible, type: itemTypes) => {
    const items = {
        [itemTypes.service]: data.service?.service.name,
        'add-on': data.addOn?.name,
        [itemTypes.product]: data.product?.name,
        [itemTypes.custom]: data.customDescription
    };
    return items[type] || undefined;
};

const getItemToDelete = (data: DataVaraible, type: itemTypes) => {
    const items = {
        [itemTypes.service]: data.service?.service?.id,
        [itemTypes.custom]: data.customDescription,
        'add-on': data.addOn?.id,
        [itemTypes.product]: data.product?.id
    };
    return items[type] || undefined;
};

export const getPriceWithDiscount = (data: DataVaraible, total?: number) => {
    if (data.itemData?.discount?.type === 'percentage' && total) {
        const amountToDiscount = (data.itemData?.discount?.value * total) / 100;
        return total - amountToDiscount;
    } else if (data.itemData?.discount?.type === 'amount' && total) {
        return total - data.itemData?.discount?.value;
    }
};

// Session-based business areas (Walking parameterised by area) share the time-range summary.
const sessionFlows: Array<ItemData['flow']> = ['walking', 'training'];

export const AppointmentCard: FunctionComponent<Props> = props => {
    const services = useSelector<RootState, Array<Service>>(state => state.services.services);

    const staffsState = useSelector<RootState, Array<Staff>>(state => state.staff.staff);

    const formatStaffToStaffMember = (staffId: number) => {
        const staffSelected = staffsState.find(staff => staff.id === staffId);
        return staffSelected?.person.firstName;
    };

    const isSessionFlow = sessionFlows.includes(props.data.itemData?.flow);
    const sessionDescription = [
        props.data.dateEnd
            ? `${props.data.date.format('h:mma')} - ${props.data.dateEnd.format('h:mma')}`
            : props.data.date.format('h:mma'),
        getItem(props.data, getType(props.data)),
        props.data.staffId ? formatStaffToStaffMember(props.data.staffId) : 'Unassigned'
    ]
        .filter(Boolean)
        .join(' · ');

    const slotLinePrice = Number(props.data.service?.price) || 0;
    const lineBase = resolveAppointmentCardLineBase({
        slotPrice: slotLinePrice,
        itemDataPrice: props.data.itemData?.price,
        beforePrice: props.data.service?.beforePrice,
        customPriceAmount: props.data.service?.customPriceAmount
    });
    const {
        displayPrice: resolvedDisplayPrice,
        originalPrice: displayPriceBeforeDiscount,
        hasDisplayDiscount
    } = resolveAppointmentCardLineDisplay({
        enrollmentId: props.data.service?.enrollment?.id,
        membershipItem: props.data.service?.membershipItem,
        slotPrice: slotLinePrice,
        basePrice: lineBase,
        itemDiscount: props.data.itemData?.discount ?? props.data.service?.discount
    });

    return (
        <>
            {props.index === 0 && (
                <div className={styles.dateContainer}>
                    <span className={styles.date}>{props.data.date.format('dddd, DD MMM YYYY')}</span>
                </div>
            )}
            <div
                className={styles.serviceStaffContainer}
                key={props.data.service?.service.id}
            >
                <div className={styles.itemContainer}>
                    <div
                        className={styles.emptyBox}
                        style={{
                            borderLeftColor:
                                services.find((serv: Service) => serv.id === props.data.service?.service?.id)?.color ??
                                '#ffffff'
                        }}
                    />
                    <div
                        className={clsx(styles.divide, {
                            [styles.withoutDivider]: props.index === props.appts.length - 1
                        })}
                        style={{ width: '91%' }}
                    >
                        <span className={styles.serviceName}>
                            <span
                                className={clsx(styles.serviceNameText, {
                                    [styles.rightSpace]: getType(props.data) === itemTypes.service
                                })}
                            >
                                {getName(props.data, getType(props.data))}
                            </span>
                            {`${props.data.itemData?.quantity ? '(' : ' · '}${
                                props.data.itemData?.quantity ? props.data.itemData?.quantity : ''
                            }${props.data.itemData?.quantity ? ') ·' : ''}`}
                            {props.data.service ? (
                                <>
                                    <div className={styles.serviceData}>
                                        {hasDisplayDiscount && (
                                            <span className={clsx(styles.beforePrice, styles.price)}>
                                                ${displayPriceBeforeDiscount.toFixed(2)}
                                            </span>
                                        )}
                                        {hasDisplayDiscount && (
                                            <span className={styles.price}>${resolvedDisplayPrice.toFixed(2)}</span>
                                        )}
                                    </div>
                                    {!hasDisplayDiscount && (
                                        <span className={styles.price}>{'$' + resolvedDisplayPrice.toFixed(2)}</span>
                                    )}
                                </>
                            ) : (
                                <div>
                                    {props.data?.itemData?.discount?.value === undefined ||
                                    props.data?.itemData?.discount?.value === 0 ? (
                                        <span className={styles.price}>${props.data?.itemData?.total?.toFixed(2)}</span>
                                    ) : (
                                        <div className={styles.serviceData}>
                                            <span className={clsx(styles.beforePrice, styles.price)}>
                                                ${props.data.itemData?.total?.toFixed(2)}
                                            </span>
                                            <span className={styles.price}>
                                                $
                                                {getPriceWithDiscount(props.data, props.data.itemData?.total)?.toFixed(
                                                    2
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </span>
                        <div className={styles.serviceDataContainer}>
                            <div className={styles.dataContainer}>
                                {isSessionFlow ? (
                                    <span className={styles.priceAndDuration}>{sessionDescription}</span>
                                ) : (
                                    <>
                                        {props.data.service && (
                                            <span className={styles.priceAndDuration}>
                                                {props.data.date.format('LT')} ·
                                            </span>
                                        )}
                                        <div className={styles.dataContainer}>
                                            <span className={clsx(styles.priceAndDuration, styles.spacingDuration)}>
                                                {getItem(props.data, getType(props.data))}
                                            </span>
                                        </div>
                                        <span className={styles.priceAndDuration}>
                                            ·{' '}
                                            {props.data.staffId
                                                ? formatStaffToStaffMember(props.data.staffId!)
                                                : 'Unassigned'}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        className={clsx(styles.divide, styles.serviceOptionsContainer, {
                            [styles.withoutDivider]: props.index === props.appts.length - 1
                        })}
                    >
                        <ServiceOptions
                            className={styles.serviceOptions}
                            item={getItem(props.data, getType(props.data), true)}
                            onClickMobile={() =>
                                props.onShowActions(
                                    props.appointment.petId,
                                    props.data.date,
                                    props.data.itemData,
                                    props.data.service,
                                    props.data.staffId,
                                    props.data.product,
                                    props.data.addOn,
                                    props.data.customDescription,
                                    getType(props.data)
                                )
                            }
                            onEdit={() =>
                                props.onEdit(
                                    props.appointment.petId,
                                    props.data.date,
                                    props.data.staffId,
                                    props.data.itemData,
                                    props.data.service,
                                    props.data.product,
                                    props.data.addOn,
                                    props.data.customDescription,
                                    getType(props.data),
                                    props.data.dateEnd
                                )
                            }
                            onDeleteAppointment={() =>
                                props.onDeleteAppointment(
                                    props.appointment.petId,
                                    getItemToDelete(props.data, getType(props.data)),
                                    getType(props.data),
                                    props.data.date
                                )
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default AppointmentCard;
